import { Link } from "react-router-dom";
import { Mail, ShieldCheck } from "lucide-react";
import { useLanguage, pick } from "@/lib/i18n";
import { FOOTER, NAV, PATHS, SUPPORT_EMAIL } from "@/lib/translations";
import { LOGO_URL } from "@/lib/brand";

export default function Footer() {
    const { lang } = useLanguage();
    const f = pick(FOOTER, lang);
    const n = pick(NAV, lang);

    const legalLinks = [
        { to: PATHS.privacy, label: n.privacy, key: "privacy" },
        { to: PATHS.terms, label: n.terms, key: "terms" },
        { to: PATHS.childSafety, label: n.childSafety, key: "child-safety" },
        { to: PATHS.cookies, label: n.cookies, key: "cookies" },
        { to: PATHS.legal, label: n.legal, key: "legal" },
    ];

    return (
        <footer
            data-testid="site-footer"
            className="bg-jungle-stripes text-cream-50 mt-24"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-6 py-16 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-cream-50 border-2 border-cream-50/20 overflow-hidden">
                            <img
                                src={LOGO_URL}
                                alt="Dino Detective logo"
                                className="w-10 h-10 object-contain"
                            />
                        </span>
                        <span className="font-display text-2xl font-bold">Dino Detective</span>
                    </div>
                    <p className="font-display text-lg text-amber-400 mb-3">
                        {f.tagline}
                    </p>
                    <p className="text-sm text-cream-50/80 leading-relaxed max-w-md">
                        {f.about}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm text-cream-50/80">
                        <ShieldCheck className="w-4 h-4 text-amber-400" strokeWidth={2.5} />
                        <span>{f.ownerLine}</span>
                    </div>
                </div>

                <div className="md:col-span-4">
                    <h4 className="font-display text-sm uppercase tracking-wider text-amber-400 mb-4">
                        {f.sections.legal}
                    </h4>
                    <ul className="space-y-2.5">
                        {legalLinks.map((l) => (
                            <li key={l.key}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-link-${l.key}`}
                                    className="text-cream-50/90 hover:text-amber-400 transition-colors text-sm font-medium"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <h4 className="font-display text-sm uppercase tracking-wider text-amber-400 mb-4">
                        {f.sections.support}
                    </h4>
                    <Link
                        to={PATHS.contact}
                        data-testid="footer-link-contact"
                        className="text-cream-50/90 hover:text-amber-400 transition-colors text-sm font-medium block mb-3"
                    >
                        {n.contact}
                    </Link>
                    <div className="text-xs uppercase tracking-wider text-cream-50/60 mb-1">
                        {f.emailLabel}
                    </div>
                    <a
                        href={`mailto:${SUPPORT_EMAIL}`}
                        data-testid="footer-support-email"
                        className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-500 font-semibold text-sm break-all"
                    >
                        <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
                        {SUPPORT_EMAIL}
                    </a>
                </div>
            </div>
            <div className="border-t border-cream-50/10">
                <div className="max-w-6xl mx-auto px-5 sm:px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-cream-50/60">
                    <span>© {new Date().getFullYear()} Dino Detective. {f.rights}</span>
                    <span>dino-detective.app</span>
                </div>
            </div>
        </footer>
    );
}
